// Field labels and descriptions are typed into plain <input>s in the event
// editor (see SortableFormFieldItem), so an admin who needs a link — consent
// copy has to point at a privacy policy or a rules page — writes one in
// markdown. Both spellings show up in real forms: the correct `[text](url)`
// and the transposed `(text)[url]`, which reads naturally enough that it is
// what the IBM Bob consent copy actually used. Render both; anything else in
// the string is left exactly as typed.
const LINK_PATTERN =
  /\[([^\]\n]+?)\]\(\s*([^()\s]+?)\s*\)|\(([^()\n]+?)\)\[\s*([^[\]\s]+?)\s*\]/g

// An admin-typed href still ends up in the DOM, so only schemes that cannot
// execute are allowed through. Anything else falls back to plain text — the
// label keeps its wording instead of turning into a live `javascript:` link.
const SAFE_URL = /^(https?:\/\/|mailto:|tel:|\/|#)/i

export interface TextSegment {
  text: string
  href?: string
}

export const parseTextLinks = (text: string): TextSegment[] => {
  const segments: TextSegment[] = []
  let lastIndex = 0

  // Fresh regex state per call: LINK_PATTERN is global, so a shared lastIndex
  // would make every other call start mid-string.
  LINK_PATTERN.lastIndex = 0

  let match: RegExpExecArray | null
  while ((match = LINK_PATTERN.exec(text)) !== null) {
    const label = match[1] ?? match[3]
    const href = match[2] ?? match[4]

    if (match.index > lastIndex) {
      segments.push({ text: text.slice(lastIndex, match.index) })
    }

    if (SAFE_URL.test(href)) {
      segments.push({ text: label, href })
    } else {
      segments.push({ text: match[0] })
    }

    lastIndex = match.index + match[0].length
  }

  if (lastIndex < text.length) {
    segments.push({ text: text.slice(lastIndex) })
  }

  return segments
}

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')

// For the one place that already renders sanitized HTML (consent descriptions).
// Only the link syntax is rewritten; the rest of the string is passed through
// untouched so any HTML a description already carries survives to DOMPurify.
export const linkifyTextToHtml = (text: string): string =>
  parseTextLinks(text)
    .map(({ text: segment, href }) =>
      href
        ? `<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">${escapeHtml(segment)}</a>`
        : segment
    )
    .join('')
